import { Dispatch, SetStateAction } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  ACCREDITATIONS,
  CAPACITY_UOM,
  SECTOR_EXPERIENCE,
  TEMPERATURE_CONTROLS,
} from '@forms/WarehouseForm/constants';
import {
  ADDITIONAL_SERVICES,
  FULFILMENT_SERVICES,
  MATERIAL_HANDLING_EQUIPMENT,
  STORAGE_CAPABILITIES,
  WAREHOUSE_OPERATIONS,
} from '@views/WarehouseDetails/constants';
import { Input } from '../../tailwindComponents/Input/Input';
import { Select } from '../../tailwindComponents/Select/Select';
import { MultiSelect } from '../../tailwindComponents/MultiSelect/MultiSelect';
import { Button } from '../../tailwindComponents/Button/Button';

// Derived from the canonical backend lists so every option here routes to a real
// filter field. The parent splits the user's selection between WarehouseOperations
// and AdditionalServices on the request using the same source constants.
const VALUE_ADDING_SERVICES = [
  ...WAREHOUSE_OPERATIONS,
  ...ADDITIONAL_SERVICES,
];

const SYSTEMS_AND_SECURITY = [
  'WMS',
  'TMS',
  'WiFi',
  'API',
  'EDI',
  'Other',
  '24/7 Security',
  '24/7 Access',
  'Bunded',
  'Caged',
  'Sprinklered',
  'Alarmed',
  'Patrolled',
  'Fenced Yard',
  'CCTV',
  'ESFR Sprinkler System',
];

const CURRENCY_OPTIONS = [
  { label: 'GBP', value: 'GBP' },
  { label: 'EUR', value: 'EUR' },
  { label: 'USD', value: 'USD' },
];

const RACKING_OPTIONS = [
  { label: '- No racking filter -', value: 'any' },
  { label: 'Has Racking', value: 'yes' },
];

const toStringOptions = (values: string[]) =>
  values.map((v) => ({ label: v, value: v }));

const fromStringOptions = (
  items: { label: string; value: string | number }[]
) => items.map((i) => String(i.value));

interface FiltersSideDrawerProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  isDesktop: boolean;
  currency: string;
  setCurrency: Dispatch<SetStateAction<string>>;
  specialCapabilities: string[];
  setSpecialCapabilities: Dispatch<SetStateAction<string[]>>;
  valueAddingServices: string[];
  setValueAddingServices: Dispatch<SetStateAction<string[]>>;
  systemsAndSecurity: string[];
  setSystemsAndSecurity: Dispatch<SetStateAction<string[]>>;
  temperatureControls: string[];
  setTemperatureControls: Dispatch<SetStateAction<string[]>>;
  sectorExperience: string[];
  setSectorExperience: Dispatch<SetStateAction<string[]>>;
  fulfilmentServices: string[];
  setFulfilmentServices: Dispatch<SetStateAction<string[]>>;
  materialHandlingEquipment: string[];
  setMaterialHandlingEquipment: Dispatch<SetStateAction<string[]>>;
  accreditationsAndMemberships: string[];
  setAccreditationsAndMemberships: Dispatch<SetStateAction<string[]>>;
  storageCapacityUnitOfMeasure: string;
  setStorageCapacityUnitOfMeasure: Dispatch<SetStateAction<string>>;
  minAvailableStorageCapacity: string;
  setMinAvailableStorageCapacity: Dispatch<SetStateAction<string>>;
  requiresRacking: boolean;
  setRequiresRacking: Dispatch<SetStateAction<boolean>>;
  minRackingHeight: string;
  setMinRackingHeight: Dispatch<SetStateAction<string>>;
  minRackingWidth: string;
  setMinRackingWidth: Dispatch<SetStateAction<string>>;
  minRackingDepth: string;
  setMinRackingDepth: Dispatch<SetStateAction<string>>;
  fromPrice: string;
  setFromPrice: Dispatch<SetStateAction<string>>;
  toPrice: string;
  setToPrice: Dispatch<SetStateAction<string>>;
  handleFilterClick: () => void;
  handleClearFilters: () => void;
}

export function FiltersSideDrawer({
  isOpen,
  setIsOpen,
  isDesktop,
  currency,
  setCurrency,
  specialCapabilities,
  setSpecialCapabilities,
  valueAddingServices,
  setValueAddingServices,
  systemsAndSecurity,
  setSystemsAndSecurity,
  temperatureControls,
  setTemperatureControls,
  sectorExperience,
  setSectorExperience,
  fulfilmentServices,
  setFulfilmentServices,
  materialHandlingEquipment,
  setMaterialHandlingEquipment,
  accreditationsAndMemberships,
  setAccreditationsAndMemberships,
  storageCapacityUnitOfMeasure,
  setStorageCapacityUnitOfMeasure,
  minAvailableStorageCapacity,
  setMinAvailableStorageCapacity,
  requiresRacking,
  setRequiresRacking,
  minRackingHeight,
  setMinRackingHeight,
  minRackingWidth,
  setMinRackingWidth,
  minRackingDepth,
  setMinRackingDepth,
  fromPrice,
  setFromPrice,
  toPrice,
  setToPrice,
  handleFilterClick,
  handleClearFilters,
}: FiltersSideDrawerProps) {
  const onClose = () => setIsOpen(false);

  const capacityUomOptions = CAPACITY_UOM.map((uom) => ({
    label: uom.label,
    value: uom.value,
  }));
  const selectedCapacityUom =
    capacityUomOptions.find((o) => o.value === storageCapacityUnitOfMeasure) ||
    null;

  const selectedCurrency =
    CURRENCY_OPTIONS.find((o) => o.value === currency) || null;

  const temperatureOptions = TEMPERATURE_CONTROLS.map((c) => ({
    label: c.value,
    value: c.value,
  }));
  const accreditationOptions = ACCREDITATIONS.filter(
    (a) => a.toLowerCase() !== 'none'
  ).map((v) => ({ label: v, value: v }));

  return (
    <>
      <div
        className={`fixed inset-0 z-drawerBackdrop transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } ${isDesktop ? '' : 'bg-black/20'}`}
        onClick={onClose}
        role='presentation'
      />

      <div
        role='dialog'
        aria-modal='true'
        aria-label='Warehouse search filters'
        className={`fixed z-drawer bg-gray-50 shadow-xl flex flex-col will-change-transform transition-transform duration-300 ease-out ${
          isDesktop
            ? `right-0 top-0 h-full w-[32rem] border-l border-gray-200 ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
            : `bottom-0 left-0 right-0 max-h-[90vh] rounded-t-2xl ${
                isOpen ? 'translate-y-0' : 'translate-y-full'
              }`
        }`}
      >
        <div
          className={`flex items-center px-6 pt-5 pb-2 ${
            isDesktop ? 'justify-start' : 'justify-end'
          }`}
        >
          <button
            type='button'
            aria-label='Close filters'
            onClick={onClose}
            className='rounded-md p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary'
          >
            <XMarkIcon className='h-6 w-6' aria-hidden='true' />
          </button>
        </div>

        <div className='flex-1 overflow-y-auto px-6 pb-6'>
          <div className='flex flex-col gap-6'>
            <section className='flex flex-col gap-3'>
              <h2 className='text-base font-medium text-gray-900'>
                Storage capacity
              </h2>
              <Select
                label='Storage Capacity Unit of Measure'
                options={capacityUomOptions}
                selected={selectedCapacityUom}
                setSelected={(option) =>
                  setStorageCapacityUnitOfMeasure(String(option.value))
                }
              />
              {storageCapacityUnitOfMeasure && (
                <Input
                  label='Required capacity'
                  name='requiredCapacity'
                  type='number'
                  placeholder='Required capacity'
                  value={minAvailableStorageCapacity}
                  onChange={(e) =>
                    setMinAvailableStorageCapacity(e.target.value)
                  }
                />
              )}
            </section>

            <hr className='border-gray-200' />

            <section className='flex flex-col gap-3'>
              <h2 className='text-base font-medium text-gray-900'>
                Storage space price per week
              </h2>
              <Select
                label='Currency'
                options={CURRENCY_OPTIONS}
                selected={selectedCurrency}
                setSelected={(option) => setCurrency(String(option.value))}
              />
              <Input
                label='FROM'
                name='fromPrice'
                placeholder='FROM'
                value={fromPrice}
                onChange={(e) => setFromPrice(e.target.value)}
                type='number'
              />
              <Input
                label='TO'
                name='toPrice'
                placeholder='TO'
                value={toPrice}
                onChange={(e) => setToPrice(e.target.value)}
                type='number'
              />
            </section>

            <hr className='border-gray-200' />

            <section className='flex flex-col gap-3'>
              <h2 className='text-base font-medium text-gray-900'>Racking</h2>
              <Select
                label='Racking'
                options={RACKING_OPTIONS}
                selected={
                  requiresRacking ? RACKING_OPTIONS[1] : RACKING_OPTIONS[0]
                }
                setSelected={(option) =>
                  setRequiresRacking(option.value === 'yes')
                }
              />
              {requiresRacking && (
                <>
                  <Input
                    label='Min Racking Height'
                    name='minRackingHeight'
                    placeholder='Min Racking Height'
                    value={minRackingHeight}
                    onChange={(e) => setMinRackingHeight(e.target.value)}
                    type='number'
                  />
                  <Input
                    label='Min Racking Width'
                    name='minRackingWidth'
                    placeholder='Min Racking Width'
                    value={minRackingWidth}
                    onChange={(e) => setMinRackingWidth(e.target.value)}
                    type='number'
                  />
                  <Input
                    label='Min Racking Depth'
                    name='minRackingDepth'
                    placeholder='Min Racking Depth'
                    value={minRackingDepth}
                    onChange={(e) => setMinRackingDepth(e.target.value)}
                    type='number'
                  />
                </>
              )}
            </section>

            <hr className='border-gray-200' />

            <MultiSelect
              label='Special and bulk goods'
              options={toStringOptions(STORAGE_CAPABILITIES)}
              selected={toStringOptions(specialCapabilities)}
              setSelected={(items) =>
                setSpecialCapabilities(fromStringOptions(items))
              }
            />
            <MultiSelect
              label='Value adding services'
              options={toStringOptions(VALUE_ADDING_SERVICES)}
              selected={toStringOptions(valueAddingServices)}
              setSelected={(items) =>
                setValueAddingServices(fromStringOptions(items))
              }
            />
            <MultiSelect
              label='Systems and security'
              options={toStringOptions(SYSTEMS_AND_SECURITY)}
              selected={toStringOptions(systemsAndSecurity)}
              setSelected={(items) =>
                setSystemsAndSecurity(fromStringOptions(items))
              }
            />
            <MultiSelect
              label='Temperature Controls'
              options={temperatureOptions}
              selected={toStringOptions(temperatureControls)}
              setSelected={(items) =>
                setTemperatureControls(fromStringOptions(items))
              }
            />
            <MultiSelect
              label='Sector Experience'
              options={toStringOptions(SECTOR_EXPERIENCE)}
              selected={toStringOptions(sectorExperience)}
              setSelected={(items) =>
                setSectorExperience(fromStringOptions(items))
              }
            />
            <MultiSelect
              label='Fulfilment Services'
              options={toStringOptions(FULFILMENT_SERVICES)}
              selected={toStringOptions(fulfilmentServices)}
              setSelected={(items) =>
                setFulfilmentServices(fromStringOptions(items))
              }
            />
            <MultiSelect
              label='Material Handling Equipment'
              options={toStringOptions(MATERIAL_HANDLING_EQUIPMENT)}
              selected={toStringOptions(materialHandlingEquipment)}
              setSelected={(items) =>
                setMaterialHandlingEquipment(fromStringOptions(items))
              }
            />
            <MultiSelect
              label='Accreditations and Memberships'
              options={accreditationOptions}
              selected={toStringOptions(accreditationsAndMemberships)}
              setSelected={(items) =>
                setAccreditationsAndMemberships(fromStringOptions(items))
              }
            />
          </div>
        </div>

        <div className='flex gap-3 border-t border-gray-200 bg-white px-6 py-4'>
          <Button
            label='Reset'
            intent='secondary'
            size='lg'
            onClick={handleClearFilters}
            className='flex-shrink-0'
          />
          <Button
            label='Filter'
            intent='primary'
            size='lg'
            onClick={handleFilterClick}
            className='w-full'
          />
        </div>
      </div>
    </>
  );
}
